import React, { useState, useEffect, useRef } from 'react';
import useImageCrop from '../hooks/useImageCrop';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Save, PenLine, Image as ImageIcon, MapPin,
  ShieldCheck, Info, Trash2, Plus, Clock, Check
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import ReactQuill from 'react-quill';
import AIReviewButton from '../components/AIReviewButton';
import { generateReportHash, generateTimestampSeal, generateAccessToken } from '../utils/crypto';
import { track } from '../utils/analytics';
import { markOnboardingActivated, readOnboardingState } from '../utils/onboarding';
import TemplatePicker from '../components/common/TemplatePicker';

/* Cabeçalho de etapa — mesma numeração 01/02/03 da landing */
const StepHeading = ({ number, title }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-display text-2xl font-bold text-navy" aria-hidden="true">{number}</span>
    <h2 className="font-semibold text-base text-ink uppercase tracking-wider">{title}</h2>
  </div>
);

const reportHasMeaningfulContent = (report) => {
  if (report?.status === 'signed') return true;
  const body = String(report?.content || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return Boolean(body || String(report?.patient_name || '').trim());
};

export default function NewReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get('templateId');

  const { templates, availableTemplates, signatures, locations, reports, fetchReports } = useData();
  const { user } = useAuth();

  // Gerado no client para servir de pasta das imagens (storage RLS exige que
  // a pasta seja um report_id do dono) e vira o próprio id do laudo ao salvar.
  const [reportId] = useState(() => crypto.randomUUID());
  // A policy de storage exige que já exista uma linha em `reports` dona da
  // pasta antes do upload — por isso criamos um rascunho mínimo na primeira
  // imagem (idempotente) em vez de só no envio final.
  const draftCreatedRef = useRef(false);
  const quillRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    patient_name: '',
    patient_age: '',
    patient_sex: '',
    patient_species: '',
    patient_breed: '',
    patient_owner: '',
    patient_is_neutered: false,
    exam_date: getCurrentDateTime(),
    requesting_professional: '',
    request_reason: '',
    template_id: '',
    signature_id: '',
    location_id: '',
    content: '',
    folder_id: null
  });

  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    if (templateIdParam && availableTemplates.length > 0) {
      const template = availableTemplates.find(t => t.id === templateIdParam);
      if (template) {
        setFormData(prev => ({
          ...prev,
          template_id: template.id,
          content: template.content || ''
        }));
      }
    }
  }, [templateIdParam, availableTemplates]);

  const handlePatientNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, patient_name: value });

    if (value.length > 2) {
      const uniquePatients = Array.from(new Set(reports.map(r => r.patient_name)))
        .filter(n => n?.toLowerCase().includes(value.toLowerCase()))
        .map(n => reports.find(r => r.patient_name === n))
        .slice(0, 5);
      
      setPatientSuggestions(uniquePatients);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectPatient = (patient) => {
    setFormData({
      ...formData,
      patient_name: patient.patient_name,
      patient_age: patient.patient_age || '',
      patient_sex: patient.patient_sex || '',
      patient_species: patient.patient_species || '',
      patient_breed: patient.patient_breed || '',
      patient_owner: patient.patient_owner || '',
      patient_is_neutered: patient.patient_is_neutered || false,
      requesting_professional: patient.requesting_professional || ''
    });
    setShowSuggestions(false);
  };

  const handleTemplateChange = (e) => {
    const id = e.target.value;
    const template = templates.find(t => t.id === id);
    setFormData({
      ...formData,
      template_id: id,
      content: template ? template.content : ''
    });
  };

  const { cropImages, cropModal } = useImageCrop();

  const handleImageUpload = async (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = '';
    if (!picked.length) return;
    const files = await cropImages(picked, { allowSkip: true });
    if (!files) return;

    setIsLoading(true);
    try {
      if (!draftCreatedRef.current) {
        const { error: draftError } = await supabase
          .from('reports')
          .insert([{ id: reportId, user_id: user.id, status: 'draft' }]);
        if (draftError) throw draftError;
        draftCreatedRef.current = true;
      }

      const newImages = [];
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${reportId}/${fileName}`;
        const { error: uploadError } = await supabase.storage.from('report-images').upload(filePath, file);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('report-images').getPublicUrl(filePath);
        newImages.push(publicUrl);
      }
      setUploadedImages([...uploadedImages, ...newImages]);
    } catch (err) {
      alert('Erro no upload: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (status = 'draft') => {
    if (!formData.patient_name || !formData.content) {
      alert('Por favor, preencha o nome do paciente e o conteúdo do laudo.');
      return;
    }
    setIsLoading(true);
    try {
      const displayDate = new Date(formData.exam_date).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });

      // Ao EMITIR (signed) o laudo ganha hash de integridade, token de acesso
      // (a "senha" que o paciente digita) e selo de tempo. É o que faz o
      // /report/:id e a validação por token funcionarem.
      let sealed = {};
      if (status === 'signed') {
        const nowIso = new Date().toISOString();
        const hash = await generateReportHash({
          content: formData.content,
          patientName: formData.patient_name,
          examDate: displayDate,
          signatureId: formData.signature_id,
          locationId: formData.location_id,
          version: 1,
        });
        sealed = {
          hash,
          access_token: generateAccessToken(6),
          timestamp_seal: await generateTimestampSeal(hash, nowIso),
          timestamp_verified_at: nowIso,
        };
      }

      const reportPayload = {
        id: reportId,
        ...formData,
        exam_date: displayDate,
        user_id: user.id,
        status,
        signed_at: status === 'signed' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
        ...sealed,
      };
      // upsert (não insert): se já subiu alguma imagem, o rascunho com este
      // id já existe (criado em handleImageUpload) — aqui só completamos os
      // demais campos, sem duplicar a linha.
      const { data: reportData, error: reportError } = await supabase.from('reports').upsert([reportPayload]).select().single();
      if (reportError) throw reportError;

      const hadMeaningfulReport = (reports || []).some((existing) => existing.id !== reportId && reportHasMeaningfulContent(existing));
      const onboarding = readOnboardingState(localStorage, user.id);
      if (hadMeaningfulReport) {
        markOnboardingActivated(localStorage, user.id);
      } else if (onboarding.status !== 'activated') {
        track('first_report_created', {
          template_id: formData.template_id || null,
          used_template: Boolean(formData.template_id),
        });
        if (markOnboardingActivated(localStorage, user.id)) {
          track('onboarding_activated', { activation_event: 'first_report_created' });
        }
      }

      if (uploadedImages.length > 0) {
        const imagesPayload = uploadedImages.map(url => ({ report_id: reportData.id, image_url: url }));
        await supabase.from('report_images').insert(imagesPayload);
      }
      await fetchReports();
      navigate('/meus-laudos');
    } catch (err) {
      alert('Erro ao salvar: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'clean'],
    ],
  };

  const isVet = user?.type === 'veterinario';
  const labelType = isVet ? 'CRMV' : 'CRM';

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-32">
      {cropModal}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="p-2.5 min-h-[44px] min-w-[44px] hover:bg-paper-alt rounded-card transition-colors" aria-label="Voltar">
          <ArrowLeft className="w-6 h-6 text-muted" aria-hidden="true" />
        </button>
        <h1 className="font-display text-2xl font-bold text-navy">Novo laudo</h1>
      </div>

      <div className="space-y-8">
        {/* Etapa 01: identificação */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <StepHeading number="01" title="Identificação do paciente" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="md:col-span-2 relative">
              <label htmlFor="patient-name" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Nome do paciente *</label>
              <div className="relative">
                <input
                  id="patient-name"
                  type="text"
                  className="input-field"
                  placeholder="Busque ou digite o nome do paciente..."
                  value={formData.patient_name}
                  onChange={handlePatientNameChange}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                <AnimatePresence>
                  {showSuggestions && patientSuggestions.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 top-full left-0 right-0 mt-1 bg-paper border border-line rounded-card shadow-xl overflow-hidden">
                      {patientSuggestions.map((p, i) => (
                        <button key={i} onClick={() => selectPatient(p)} className="w-full text-left px-4 py-3 min-h-[44px] hover:bg-paper-alt flex items-center justify-between transition-colors border-b border-line last:border-0">
                          <div>
                            <span className="block font-medium text-ink">{p.patient_name}</span>
                            <span className="text-[10px] text-muted uppercase">{isVet ? `Tutor: ${p.patient_owner || 'N/A'}` : `Responsável: ${p.patient_owner || 'N/A'}`}</span>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div>
              <label htmlFor="patient-owner" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">{isVet ? 'Tutor / Proprietário' : 'Responsável'}</label>
              <input id="patient-owner" type="text" className="input-field" value={formData.patient_owner} onChange={(e) => setFormData({ ...formData, patient_owner: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="patient-age" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Idade</label>
                <input id="patient-age" type="text" className="input-field" value={formData.patient_age} onChange={(e) => setFormData({ ...formData, patient_age: e.target.value })} />
              </div>
              <div>
                <label htmlFor="patient-sex" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Sexo</label>
                <select id="patient-sex" className="input-field appearance-none" value={formData.patient_sex} onChange={(e) => setFormData({ ...formData, patient_sex: e.target.value })}>
                  <option value="">Selecione</option>
                  <option value="M">Macho</option>
                  <option value="F">Fêmea</option>
                </select>
              </div>
            </div>
            {isVet && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Espécie / Raça</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" className="input-field" placeholder="Espécie" aria-label="Espécie" value={formData.patient_species} onChange={(e) => setFormData({ ...formData, patient_species: e.target.value })} />
                    <input type="text" className="input-field" placeholder="Raça" aria-label="Raça" value={formData.patient_breed} onChange={(e) => setFormData({ ...formData, patient_breed: e.target.value })} />
                  </div>
                </div>
                <div className="flex flex-col justify-end">
                  <button type="button" onClick={() => setFormData({ ...formData, patient_is_neutered: !formData.patient_is_neutered })} className={`flex items-center justify-between px-4 py-2.5 min-h-[44px] rounded-card border transition-colors ${formData.patient_is_neutered ? 'bg-paper-alt border-navy text-navy' : 'bg-paper border-line text-muted hover:border-navy/40'}`}>
                    <span className="text-xs font-semibold uppercase">Castrado?</span>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.patient_is_neutered ? 'border-navy bg-navy' : 'border-line'}`}>{formData.patient_is_neutered && <Check className="w-3 h-3 text-white" aria-hidden="true" />}</div>
                  </button>
                </div>
              </>
            )}
            <div>
              <label htmlFor="exam-date" className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">Data e hora do exame</label>
              <div className="relative">
                <input id="exam-date" type="datetime-local" className="input-field" value={formData.exam_date} onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })} />
              </div>
            </div>
          </div>
        </section>

        {/* Etapa 02: conteúdo e fotos */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <StepHeading number="02" title="Conteúdo do laudo" />
            <div className="flex items-center gap-3">
              <label htmlFor="template-select" className="text-xs font-semibold text-muted uppercase tracking-wide">Modelo:</label>
              <TemplatePicker id="template-select" templates={templates} value={formData.template_id} onChange={handleTemplateChange} placeholder="Nenhum selecionado" />
            </div>
          </div>

          <AIReviewButton quillRef={quillRef} reportId={reportId} />
          <div className="rich-editor-wrapper">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.content}
              onChange={(val) => setFormData({ ...formData, content: val })}
              modules={quillModules}
              placeholder="Descreva os achados do exame aqui..."
            />
          </div>

          <div className="mt-8">
            <h3 className="mb-4 text-navy font-semibold text-sm uppercase tracking-wider">Documentação fotográfica</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {uploadedImages.map((url, i) => (
                <div key={i} className="relative group aspect-square rounded-card overflow-hidden bg-paper-alt border border-line">
                  <img src={url} alt={`Anexo ${i+1}`} className="w-full h-full object-cover" />
                  <button onClick={() => setUploadedImages(uploadedImages.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-full opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity" aria-label={`Remover anexo ${i+1}`}>
                    <Trash2 className="w-3 h-3" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-card border-2 border-dashed border-line hover:border-navy hover:bg-paper-alt flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors text-muted hover:text-navy">
                <Plus className="w-6 h-6" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase">Adicionar</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            </div>
          </div>
        </section>

        {/* Etapa 03: assinatura e local */}
        <section className="bg-paper rounded-card p-6 shadow-card border border-line">
          <StepHeading number="03" title="Assinatura e local" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <span className="block text-xs font-semibold text-muted uppercase tracking-wide mb-3">Sua assinatura</span>
              <div className="grid grid-cols-1 gap-3">
                {signatures.length > 0 ? signatures.map(sig => (
                  <button key={sig.id} onClick={() => setFormData({ ...formData, signature_id: sig.id })} className={`flex items-center gap-4 p-4 rounded-card border transition-colors text-left ${formData.signature_id === sig.id ? 'border-navy bg-paper-alt' : 'border-line hover:border-navy/40'}`}>
                    {sig.image_url ? <img src={sig.image_url} alt={sig.name} className="w-12 h-12 object-contain bg-white rounded border border-line" /> : <div className="w-12 h-12 bg-paper-alt rounded flex items-center justify-center text-muted"><PenLine className="w-6 h-6" aria-hidden="true" /></div>}
                    <div><span className="block font-semibold text-ink">{sig.name}</span><span className="text-xs text-muted font-mono">{labelType} {sig.crm}/{sig.uf}</span></div>
                  </button>
                )) : <button onClick={() => navigate('/app/assinaturas')} className="p-4 min-h-[44px] border-2 border-dashed border-line rounded-card text-muted hover:text-navy hover:border-navy/40 transition-colors text-sm font-medium">+ Configurar assinatura</button>}
              </div>
            </div>
            <div>
              <span className="block text-xs font-semibold text-muted uppercase tracking-wide mb-3">Local de atendimento</span>
              <div className="grid grid-cols-1 gap-3">
                {locations.length > 0 ? locations.map(loc => (
                  <button key={loc.id} onClick={() => setFormData({ ...formData, location_id: loc.id })} className={`flex items-center gap-4 p-4 rounded-card border transition-colors text-left ${formData.location_id === loc.id ? 'border-navy bg-paper-alt' : 'border-line hover:border-navy/40'}`}>
                    {loc.logo_url ? <img src={loc.logo_url} alt={loc.name} className="w-12 h-12 object-contain bg-white rounded border border-line" /> : <div className="w-12 h-12 bg-paper-alt rounded flex items-center justify-center text-muted"><MapPin className="w-6 h-6" aria-hidden="true" /></div>}
                    <div><span className="block font-semibold text-ink">{loc.name}</span><span className="text-xs text-muted line-clamp-1">{loc.address}</span></div>
                  </button>
                )) : <button onClick={() => navigate('/app/locais')} className="p-4 min-h-[44px] border-2 border-dashed border-line rounded-card text-muted hover:text-navy hover:border-navy/40 transition-colors text-sm font-medium">+ Cadastrar clínica ou hospital</button>}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Barra de emissão: rascunho é neutro; o ÚNICO botão selo da tela é a emissão (ação irreversível, gera hash) */}
      <div className="fixed bottom-0 left-0 right-0 bg-paper/90 backdrop-blur-md border-t border-line p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-end gap-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button onClick={() => handleSubmit('draft')} disabled={isLoading} className="btn-secondary flex-1 sm:flex-none">Salvar rascunho</button>
            <button onClick={() => handleSubmit('signed')} disabled={isLoading || !formData.signature_id} className="btn-convert flex-1 sm:flex-none px-8">Emitir e registrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}